/**
 * Backend client — typed wrappers over /api/*.
 *
 * All fetches go through `apiFetch`, which surfaces non-2xx as
 * throws so React Query treats them as errors. Auth: HTTP Basic is
 * forwarded automatically by the browser once the operator's logged
 * in via the browser dialog.
 */

export type Language = 'EN' | 'AR';

export type Healthz = {
  ok: boolean;
  version: string;
  port: number;
  auth_configured: boolean;
};

export type CellListItem = {
  cell_slug: string;
  bank: string;
  product: string;
  bucket: string;
  languages_present: Language[];
  prompt_count: number;
  persona_count: number;
};

export type PersonaListItem = {
  persona_id: string;
  name: string;
  call_type: string;
  expected_path: string;
  tone: string;
  tactics_count: number;
  watch_for_count: number;
};

export type PersonaDetail = {
  id: string;
  name: string;
  bank: string;
  product: string;
  bucket: string;
  language: string;
  call_type: string;
  expected_path: string;
  merged_tactics: string[];
  tone: string;
  scenario: string;
  script: string[];
  watch_for: string[];
  dummies: Record<string, string>;
};

export type VariableKind = 'operator_declared' | 'auto_handled';

export type ExtractedVariable = {
  name: string;
  raw: string;
  kind: VariableKind;
  occurrences: number;
  definition_md: string;
  example_from_dict: string;
};

export type VariableDefinitionKind = 'fixed' | 'per_persona' | 'skip';

export type VariableDefinition = {
  name: string;
  kind: VariableDefinitionKind;
  description: string;
  fixed_value: string | null;
  per_persona_values: Record<string, string>;
};

export type PromptListItem = {
  name: string;
  version: number;
  cell_slug: string;
  language: Language;
  source_filename: string;
  body_length: number;
  variable_count: number;
  operator_declared_count: number;
  owner_email: string | null;
  deleted_at: string | null;
  deleted_by: string | null;
  agent_type: string | null;
};

export type PromptDetail = {
  name: string;
  version: number;
  cell_slug: string;
  language: Language;
  source_filename: string;
  source_content_hash: string;
  body: string;
  body_length: number;
  line_count: number;
  variables: ExtractedVariable[];
  variable_definitions: VariableDefinition[];
  owner_email: string | null;
  deleted_at: string | null;
  deleted_by: string | null;
  agent_type: string | null;
};

export type AgentTypeOption = { slug: string; label: string };

/** Client-side mirror of backend services/agent_types.normalize_call_type
 *  so run-start can pre-select matching personas without a round-trip.
 *  Unknown tags return null and must be treated as "matches anything". */
export function normalizeCallType(raw: string | null | undefined): string | null {
  const n = (raw ?? '').toLowerCase().replace(/[^a-z0-9]/g, '');
  if (!n) return null;
  const aliases: Record<string, string> = {
    initial: 'initial', ptpinitial: 'initial', initialcall: 'initial', firstcall: 'initial',
    rtp: 'rtp', followuprtp: 'rtp', rtpfollowup: 'rtp', refusetopay: 'rtp', followup: 'rtp',
    reminder: 'reminder', ptpreminder: 'reminder',
    brokenptp: 'broken_ptp', brokenpromise: 'broken_ptp', bptp: 'broken_ptp',
    predue: 'predue', preduereminder: 'predue', predu: 'predue',
    callback: 'callback', callbackagent: 'callback',
  };
  return aliases[n] ?? null;
}

export type PersonaState = {
  today: string;
  cell_slug: string;
  language: Language;
  persona_id: string;
  persona_name: string;
  dpd: number;
  due_date: string | null;
  next_due_date: string | null;
  statement_date: string | null;
  month_end: string | null;
  latest_allowed_ptp_date: string | null;
  overdue_amount: string | null;
  installment_amount: string | null;
  two_installments_amount: string | null;
  total_outstanding: string | null;
  title: string | null;
  first_name: string | null;
  last_name: string | null;
  agent_name: string | null;
  reason_non_payment: string | null;
  ptp_date: string | null;
  ptp_amount: string | null;
  ptp_solution: string | null;
  p1d1_deferred: string | null;
  p2d2_deferred: string | null;
  extras: Record<string, string>;
};

export type DeriveResponse = {
  today: string;
  cell_slug: string;
  language: Language;
  prompt_name: string;
  prompt_version: number;
  states: Record<string, PersonaState>;
  /** Per-persona, per-variable resolved values for the cockpit's
   *  variable-form prefill. Empty string means no resolution — operator
   *  must type a value. */
  resolved: Record<string, Record<string, string>>;
  /** Preflight blocking errors. Empty array = Start run is enabled. */
  preflight_errors: string[];
};

/**
 * Pages we don't want to redirect away from when a 401 fires (the
 * /login page itself, the /register page). For everything else, a 401
 * means "session expired" and we send the operator to /login.
 */
const NO_REDIRECT_PATHS = new Set(['/login', '/register']);

/**
 * Read the FastAPI ``detail`` string from a non-2xx response if there
 * is one, falling back to the raw text. Lets the cockpit toast surface
 * the backend's policy messages cleanly (e.g. "Only the owner (alice)
 * can delete an active prompt. Ask them to delete it first.") instead
 * of the raw ``{"detail":"..."}`` JSON.
 */
async function _readDetail(r: Response): Promise<string> {
  const text = await r.text().catch(() => '');
  try {
    const parsed = JSON.parse(text);
    if (parsed && typeof parsed.detail === 'string') return parsed.detail;
  } catch {
    /* not JSON, fall through */
  }
  return text.slice(0, 300) || `${r.status} ${r.statusText}`;
}

async function apiFetch<T>(input: string, init?: RequestInit): Promise<T> {
  const r = await fetch(input, {
    credentials: 'include',
    headers: { 'content-type': 'application/json', ...init?.headers },
    ...init,
  });
  if (r.status === 401) {
    // Don't auto-redirect on the auth endpoints themselves — the
    // login form needs to be able to show "invalid credentials" inline.
    const path = typeof window !== 'undefined' ? window.location.pathname : '';
    const isAuthEndpoint =
      input.startsWith('/api/auth/login') || input.startsWith('/api/auth/register');
    if (!isAuthEndpoint && !NO_REDIRECT_PATHS.has(path)) {
      const next = window.location.pathname + window.location.search;
      window.location.assign(`/login?next=${encodeURIComponent(next)}`);
    }
  }
  if (!r.ok) {
    const text = await r.text().catch(() => '');
    throw new Error(`${r.status} ${r.statusText}: ${text.slice(0, 200)}`);
  }
  return r.json() as Promise<T>;
}

// ────────────────────────────────────────────────────────────────────
// Runs / Comments
// ────────────────────────────────────────────────────────────────────

export type RunStatus = 'queued' | 'running' | 'completed' | 'failed' | 'aborted';
export type WatchForOutcome = 'pass' | 'fail' | 'n/a';

export type Turn = {
  index: number;
  role: 'agent' | 'customer';
  text: string;
  ts: string;
  cost_usd: number;
  model: string;
};

export type WatchForVerdict = {
  watch_for_index: number;
  watch_for_text: string;
  outcome: WatchForOutcome;
  evidence_quote: string;
  reasoning: string;
  severity?: 'critical' | 'major' | 'minor' | null;
  source?: 'persona' | 'shared';
};

export type JudgeVerdict = {
  persona_id: string;
  overall: WatchForOutcome;
  pass_count: number;
  fail_count: number;
  na_count: number;
  watch_for_verdicts: WatchForVerdict[];
  judge_cost_usd: number;
  judge_model: string;
  judged_at: string;
  judge_today: string | null;
  evaluation_status?: 'complete' | 'incomplete' | 'not_evaluated' | 'legacy_unverified';
};

export type ConversationSim = {
  persona_id: string;
  persona_name: string;
  cell_slug: string;
  language: Language;
  prompt_name: string;
  prompt_version: number;
  rendered_prompt_hash: string;
  turns: Turn[];
  started_at: string;
  completed_at: string | null;
  turn_cap_hit: boolean;
  disconnect_fired: boolean;
  aborted_reason: string | null;
  total_input_tokens: number;
  total_output_tokens: number;
  total_cost_usd: number;
  persona_state: PersonaState | null;
  rendered_prompt_preview: string;
  rendered_prompt?: string;
  rendered_prompt_complete?: boolean;
};

export type PersonaRunResult = {
  persona_id: string;
  persona_name: string;
  sim: ConversationSim | null;
  verdict: JudgeVerdict | null;
  total_cost_usd: number;
  error: string | null;
};

export type Run = {
  run_id: string;
  cell_slug: string;
  language: Language;
  prompt_name: string;
  prompt_version: number;
  prompt_content_hash: string;
  started_at: string;
  completed_at: string | null;
  status: RunStatus;
  started_by: string;
  persona_results: PersonaRunResult[];
  error: string | null;
  today: string | null;
  scribe_version: string;
  deleted_at: string | null;
  deleted_by: string | null;
  agent_type?: string | null;
  imported?: boolean;
};

export type RunSummary = {
  run_id: string;
  cell_slug: string;
  language: Language;
  prompt_name: string;
  prompt_version: number;
  status: RunStatus;
  started_at: string;
  completed_at: string | null;
  persona_total: number;
  persona_completed: number;
  pass_count: number;
  fail_count: number;
  pass_rate: number;
  total_cost_usd: number;
  today: string | null;
  scribe_version: string;
  started_by: string;
  deleted_at: string | null;
  deleted_by: string | null;
  imported?: boolean;
};

export type CommentKind = 'operator_comment' | 'human_verdict';
export type HumanOutcome = 'pass' | 'fail' | 'n/a';

export type ReviewComment = {
  comment_id: string;
  rid: string;
  persona_id: string;
  watch_for_index: number;
  watch_for_text: string;
  author: string;
  body: string;
  created_at: string;
  criterion_ids: string[];
  evidence_quote: string;
  scribe_version: string;
  comment_kind: CommentKind;
  turn_index: number | null;
  human_outcome: HumanOutcome | null;
};

export type SimilarFinding = {
  rid: string;
  persona_id: string;
  watch_for_index: number;
  watch_for_text: string;
  evidence_quote: string;
  jaccard_score: number;
  scribe_version: string;
};

export type RegisterType = 'rulebook' | 'conversation_design' | 'verbalism';

export type RegisterContent = {
  cell_slug: string;
  register_type: RegisterType;
  body: string;
  body_length: number;
  line_count: number;
  last_modified: string | null;
};

export type VarDictContent = {
  bank: 'ENBD' | 'EI';
  body: string;
  body_length: number;
  line_count: number;
  last_modified: string | null;
};

export type ModelEntry = { id: string; label: string };
export type ModelCatalogue = {
  agent: ModelEntry[];
  persona: ModelEntry[];
  judge: ModelEntry[];
  default_temperature: { agent: number; persona: number; judge: number };
  default_model: { agent: string; persona: string; judge: string };
};

export type UserRole = 'user' | 'admin';

export type UserPublic = {
  email: string;
  role: UserRole;
  created_at: string;
};

// ── Catalogue (banks / products / buckets / cells) ──────────────────
export type CodeName = { code: string; name: string };
export type CatalogueCell = {
  slug: string;
  bank: string;
  product: string;
  bucket: string;
  enabled: boolean;
};
export type Catalogue = {
  version: number;
  banks: CodeName[];
  products: CodeName[];
  buckets: CodeName[];
  cells: CatalogueCell[];
};

export type AddBankRequest = {
  code: string;
  name: string;
  products: { code: string; name?: string }[];
  buckets: { code: string; name?: string }[];
};

export type PersonaUploadResult = {
  added: string[];
  skipped_existing: string[];
  invalid: { index: number; id: string | null; error: string }[];
  written: boolean;
  persona_count: number;
};

export type PersonaUploadSpec = {
  required_fields: { name: string; note: string }[];
  optional_fields: { name: string; note: string }[];
  example_persona: Record<string, unknown>;
  envelope_example: Record<string, unknown>;
  notes: string[];
};

// ── Remediation (v10) ───────────────────────────────────────────────
export type RemediationStatus = 'running' | 'completed' | 'failed' | 'aborted';

export type HeldEdit = {
  edit_id: string;
  find: string;
  replace: string;
  why: string;
  rule_ref: string;
  held_reason: string;
  approved: boolean;
};

export type RemediationIteration = {
  iteration: number;
  edits_proposed: number;
  edits_applied: number;
  edits_skipped: number;
  edits_held: number;
  resolved: number;
  n0: number;
  pct: number;
  new_issues: number;
  banked_version: number | null;
  cost_usd: number;
  still_failing: string[];
  new_failing: string[];
  note: string;
};

export type RemediationState = {
  rid: string;
  cell_slug: string;
  language: Language;
  prompt_name: string;
  prompt_version: number;
  status: RemediationStatus;
  phase: string;
  iteration: number;
  max_iters: number;
  target_pct: number;
  regrade_n: number;
  n0: number;
  history: RemediationIteration[];
  best_pct: number;
  best_new_issues: number;
  best_version: number | null;
  held_audit_sensitive: HeldEdit[];
  total_cost_usd: number;
  rewrite_model: string | null;
  started_by: string;
  started_at: string;
  updated_at: string;
  completed_at: string | null;
  error: string | null;
};

export type StartRemediationRequest = {
  max_iters?: number;
  target_pct?: number;
  regrade_n?: number;
  rewrite_model?: string | null;
  rewrite_temperature?: number | null;
  judge_model?: string | null;
  judge_temperature?: number | null;
};

// ── Judge calibrations (overrule queue) ─────────────────────────────
export type CalibrationStatus = 'pending' | 'approved' | 'rejected';

export type JudgeCalibration = {
  calibration_id: string;
  cell_slug: string;
  language: Language;
  rid: string;
  persona_id: string;
  watch_for_index: number;
  watch_for_text: string;
  judge_evidence_quote: string;
  judge_reasoning: string;
  reason: string;
  status: CalibrationStatus;
  requested_by: string;
  requested_at: string;
  decided_by: string | null;
  decided_at: string | null;
};

async function _uploadFile<T>(url: string, file: File): Promise<T> {
  const form = new FormData();
  form.append('file', file);
  const r = await fetch(url, { method: 'POST', credentials: 'include', body: form });
  if (!r.ok) {
    throw new Error(await _readDetail(r));
  }
  return r.json() as Promise<T>;
}

export const api = {
  healthz: () => apiFetch<Healthz>('/api/healthz'),

  // ──────────────────────────────────────────────────────────────
  // Auth — register / login / logout / me
  // ──────────────────────────────────────────────────────────────

  authRegister: (req: { email: string; password: string }) =>
    apiFetch<UserPublic>('/api/auth/register', {
      method: 'POST',
      body: JSON.stringify(req),
    }),

  authLogin: (req: { email: string; password: string }) =>
    apiFetch<UserPublic>('/api/auth/login', {
      method: 'POST',
      body: JSON.stringify(req),
    }),

  authLogout: async (): Promise<void> => {
    const r = await fetch('/api/auth/logout', {
      method: 'POST',
      credentials: 'include',
    });
    if (!r.ok && r.status !== 204) {
      throw new Error(`${r.status} ${r.statusText}`);
    }
  },

  authMe: () => apiFetch<UserPublic>('/api/auth/me'),

  adminListUsers: () => apiFetch<UserPublic[]>('/api/auth/users'),

  adminCreateUser: (req: { email: string; password: string; role: UserRole }) =>
    apiFetch<UserPublic>('/api/auth/admin/users', {
      method: 'POST',
      body: JSON.stringify(req),
    }),

  listCells: () => apiFetch<{ cells: CellListItem[] }>('/api/cells'),

  // ──────────────────────────────────────────────────────────────
  // Catalogue + onboarding + uploads
  // ──────────────────────────────────────────────────────────────

  getCatalogue: () => apiFetch<Catalogue>('/api/catalogue'),

  addBank: (req: AddBankRequest) =>
    apiFetch<Catalogue>('/api/catalogue/banks', {
      method: 'POST',
      body: JSON.stringify(req),
    }),

  uploadCellRegister: (cell: string, type: string, file: File) =>
    _uploadFile<RegisterContent>(
      `/api/registers/cell/${encodeURIComponent(cell)}/${encodeURIComponent(type)}/upload`,
      file,
    ),

  uploadVardict: (bank: string, file: File) =>
    _uploadFile<VarDictContent>(
      `/api/registers/bank/${encodeURIComponent(bank)}/variable_dictionary/upload`,
      file,
    ),

  uploadPersonas: (cell: string, lang: Language, file: File) =>
    _uploadFile<PersonaUploadResult>(
      `/api/personas/${encodeURIComponent(cell)}/${lang}/upload`,
      file,
    ),

  getPersonaUploadSpec: () =>
    apiFetch<PersonaUploadSpec>('/api/personas/upload-spec'),

  listPersonas: (cell: string, lang: Language) =>
    apiFetch<{
      cell_slug: string;
      language: Language;
      persona_count: number;
      personas: PersonaListItem[];
    }>(`/api/personas/${encodeURIComponent(cell)}/${lang}`),

  getPersona: (cell: string, lang: Language, pid: string) =>
    apiFetch<PersonaDetail>(
      `/api/personas/${encodeURIComponent(cell)}/${lang}/${encodeURIComponent(pid)}`,
    ),

  listPrompts: (cell: string, lang: Language) =>
    apiFetch<{
      cell_slug: string;
      language: Language;
      count: number;
      prompts: PromptListItem[];
    }>(`/api/prompts/${encodeURIComponent(cell)}/${lang}`),

  getPrompt: (cell: string, lang: Language, name: string, version: number) =>
    apiFetch<PromptDetail>(
      `/api/prompts/${encodeURIComponent(cell)}/${lang}/${encodeURIComponent(name)}/${version}`,
    ),

  listPromptVersions: (cell: string, lang: Language, name: string) =>
    apiFetch<number[]>(
      `/api/prompts/${encodeURIComponent(cell)}/${lang}/${encodeURIComponent(name)}/versions`,
    ),

  /** Ask Sonnet to generate per-persona values for each unknown variable
   *  in the prompt body. Returns suggested VariableDefinitions the
   *  cockpit pre-fills into the wizard / definitions panel — the
   *  operator reviews / edits before saving. */
  autoDefinePrompt: (
    cell: string,
    lang: Language,
    req: { body: string; unknown_variable_names: string[]; description_hints?: Record<string, string> },
  ) =>
    apiFetch<{ definitions: VariableDefinition[] }>(
      `/api/prompts/${encodeURIComponent(cell)}/${lang}/auto-define`,
      { method: 'POST', body: JSON.stringify(req) },
    ),

  createPrompt: (
    cell: string,
    lang: Language,
    req: {
      name: string;
      body: string;
      source_filename?: string;
      variable_definitions?: VariableDefinition[];
      agent_type?: string | null;
    },
  ) =>
    apiFetch<PromptDetail>(`/api/prompts/${encodeURIComponent(cell)}/${lang}`, {
      method: 'POST',
      body: JSON.stringify(req),
    }),

  listAgentTypes: () => apiFetch<AgentTypeOption[]>('/api/prompts/agent-types'),

  /** Judge a pasted REAL call transcript against this prompt's cell
   *  rulebooks. Produces a one-call 'imported' run; returns its id. */
  judgeTranscript: (
    cell: string,
    lang: Language,
    name: string,
    version: number,
    body: {
      transcript_text: string;
      facts?: Record<string, string>;
      judge_model?: string | null;
      judge_temperature?: number | null;
    },
  ) =>
    apiFetch<{
      run_id: string;
      status: string;
      imported: boolean;
      turn_count: number;
      finding_count: number;
      error: string | null;
    }>(
      `/api/prompts/${encodeURIComponent(cell)}/${lang}/${encodeURIComponent(name)}/${version}/judge-transcript`,
      { method: 'POST', body: JSON.stringify(body) },
    ),

  /** Tag an existing prompt with its agent type (all versions).
   *  null clears the tag. Owner or admin. */
  setPromptAgentType: (
    cell: string,
    lang: Language,
    name: string,
    agentType: string | null,
  ) =>
    apiFetch<{ name: string; agent_type: string | null; versions_updated: number }>(
      `/api/prompts/${encodeURIComponent(cell)}/${lang}/${encodeURIComponent(name)}/agent-type`,
      { method: 'POST', body: JSON.stringify({ agent_type: agentType }) },
    ),

  /** Shared per-call-type judge checklists for one cell:
   *  {agent_type: {language: rows[]}}. Admin. */
  getSharedWatchFor: (cell: string) =>
    apiFetch<Record<string, Record<string, string[]>>>(
      `/api/registers/cell/${encodeURIComponent(cell)}/shared-watch-for`,
    ),

  putSharedWatchFor: (cell: string, agentType: string, lang: Language, rows: string[]) =>
    apiFetch<{ rows: string[] }>(
      `/api/registers/cell/${encodeURIComponent(cell)}/shared-watch-for/${agentType}/${lang}`,
      { method: 'PUT', body: JSON.stringify({ rows }) },
    ),

  /** LLM-suggest shared rows from the cell's conversation design.
   *  Returns suggestions only — nothing is saved until PUT. Admin. */
  generateSharedWatchFor: (cell: string, agentType: string, lang: Language) =>
    apiFetch<{ suggested_rows: string[] }>(
      `/api/registers/cell/${encodeURIComponent(cell)}/shared-watch-for/generate`,
      { method: 'POST', body: JSON.stringify({ agent_type: agentType, language: lang }) },
    ),

  /** Edits the named prompt body OR definitions. Auto-bumps to next
   *  version (operator-locked policy 2026-06-08). Prior versions stay
   *  listed read-only. */
  bumpPromptVersion: (
    cell: string,
    lang: Language,
    name: string,
    req: { body: string; variable_definitions?: VariableDefinition[] },
  ) =>
    apiFetch<PromptDetail>(
      `/api/prompts/${encodeURIComponent(cell)}/${lang}/${encodeURIComponent(name)}`,
      { method: 'PUT', body: JSON.stringify(req) },
    ),

  deletePrompt: async (cell: string, lang: Language, name: string): Promise<void> => {
    const r = await fetch(
      `/api/prompts/${encodeURIComponent(cell)}/${lang}/${encodeURIComponent(name)}`,
      { method: 'DELETE', credentials: 'include' },
    );
    if (!r.ok) {
      throw new Error(await _readDetail(r));
    }
  },

  deletePromptVersion: async (
    cell: string,
    lang: Language,
    name: string,
    version: number,
  ): Promise<void> => {
    const r = await fetch(
      `/api/prompts/${encodeURIComponent(cell)}/${lang}/${encodeURIComponent(name)}/${version}`,
      { method: 'DELETE', credentials: 'include' },
    );
    if (!r.ok) {
      throw new Error(await _readDetail(r));
    }
  },

  promotePromptVersion: (
    cell: string,
    lang: Language,
    name: string,
    version: number,
  ) =>
    apiFetch<PromptDetail>(
      `/api/prompts/${encodeURIComponent(cell)}/${lang}/${encodeURIComponent(name)}/${version}/promote`,
      { method: 'POST' },
    ),

  diffPromptVersions: (
    cell: string,
    lang: Language,
    name: string,
    from_version: number,
    to_version: number,
  ) =>
    apiFetch<{
      name: string;
      from_version: number;
      to_version: number;
      from_body: string;
      to_body: string;
      unified_diff: string;
    }>(
      `/api/prompts/${encodeURIComponent(cell)}/${lang}/${encodeURIComponent(name)}/diff/${from_version}/${to_version}`,
    ),

  deriveState: (req: {
    cell_slug: string;
    language: Language;
    prompt_name: string;
    prompt_version: number;
    persona_ids: string[];
    today?: string;
    overrides?: Record<string, Record<string, string>>;
    dpd_overrides?: Record<string, number>;
  }) =>
    apiFetch<DeriveResponse>('/api/state/derive', {
      method: 'POST',
      body: JSON.stringify(req),
    }),

  startRun: (req: {
    cell_slug: string;
    language: Language;
    prompt_name: string;
    prompt_version: number;
    persona_ids: string[];
    today?: string;
    overrides?: Record<string, Record<string, string>>;
    dpd_overrides?: Record<string, number>;
    max_turns?: number;
    agent_model?: string | null;
    persona_model?: string | null;
    judge_model?: string | null;
    agent_temperature?: number | null;
    persona_temperature?: number | null;
    judge_temperature?: number | null;
  }) =>
    apiFetch<Run>('/api/runs', {
      method: 'POST',
      body: JSON.stringify(req),
    }),

  listModels: () => apiFetch<ModelCatalogue>('/api/models'),

  listRuns: () => apiFetch<{ runs: RunSummary[] }>('/api/runs'),

  getRun: (rid: string) => apiFetch<Run>(`/api/runs/${encodeURIComponent(rid)}`),

  deleteRun: async (rid: string): Promise<void> => {
    const r = await fetch(`/api/runs/${encodeURIComponent(rid)}`, {
      method: 'DELETE',
      credentials: 'include',
    });
    if (!r.ok) {
      throw new Error(await _readDetail(r));
    }
  },

  // ──────────────────────────────────────────────────────────────
  // Remediation (v10, admin-only)
  // ──────────────────────────────────────────────────────────────

  startRemediation: (rid: string, req: StartRemediationRequest = {}) =>
    apiFetch<RemediationState>(`/api/runs/${encodeURIComponent(rid)}/remediate`, {
      method: 'POST',
      body: JSON.stringify(req),
    }),

  /** null when this run has never been remediated (404). */
  getRemediation: async (rid: string): Promise<RemediationState | null> => {
    const r = await fetch(`/api/runs/${encodeURIComponent(rid)}/remediation`, {
      credentials: 'include',
    });
    if (r.status === 404) return null;
    if (!r.ok) throw new Error(await _readDetail(r));
    return r.json() as Promise<RemediationState>;
  },

  abortRemediation: (rid: string) =>
    apiFetch<RemediationState>(
      `/api/runs/${encodeURIComponent(rid)}/remediation/abort`,
      { method: 'POST' },
    ),

  approveRemediationEdits: (rid: string, editIds: string[]) =>
    apiFetch<RemediationState>(
      `/api/runs/${encodeURIComponent(rid)}/remediation/approve-edits`,
      { method: 'POST', body: JSON.stringify({ edit_ids: editIds }) },
    ),

  // ──────────────────────────────────────────────────────────────
  // Judge calibrations — "judge is wrong" overrule queue
  // ──────────────────────────────────────────────────────────────

  createCalibration: (req: {
    rid: string;
    persona_id: string;
    watch_for_index: number;
    reason: string;
  }) =>
    apiFetch<JudgeCalibration>('/api/calibrations', {
      method: 'POST',
      body: JSON.stringify(req),
    }),

  listCalibrations: (status?: CalibrationStatus) =>
    apiFetch<{ calibrations: JudgeCalibration[] }>(
      `/api/calibrations${status ? `?status=${status}` : ''}`,
    ),

  decideCalibration: (calibrationId: string, decision: 'approved' | 'rejected') =>
    apiFetch<JudgeCalibration>(
      `/api/calibrations/${encodeURIComponent(calibrationId)}/decide`,
      { method: 'POST', body: JSON.stringify({ decision }) },
    ),

  pauseRun: (rid: string) =>
    apiFetch<Run>(`/api/runs/${encodeURIComponent(rid)}/pause`, { method: 'POST' }),

  resumeRun: (rid: string) =>
    apiFetch<Run>(`/api/runs/${encodeURIComponent(rid)}/resume`, { method: 'POST' }),

  listComments: (rid: string) =>
    apiFetch<{ rid: string; comments: ReviewComment[] }>(
      `/api/runs/${encodeURIComponent(rid)}/comments`,
    ),

  addComment: (
    rid: string,
    req: {
      persona_id: string;
      watch_for_index: number;
      body: string;
      evidence_quote?: string;
      criterion_ids?: string[];
      comment_kind?: CommentKind;
      turn_index?: number | null;
      human_outcome?: HumanOutcome | null;
    },
  ) =>
    apiFetch<ReviewComment>(`/api/runs/${encodeURIComponent(rid)}/comments`, {
      method: 'POST',
      body: JSON.stringify(req),
    }),

  findSimilar: (rid: string, commentId: string) =>
    apiFetch<{ source_comment_id: string; findings: SimilarFinding[] }>(
      `/api/runs/${encodeURIComponent(rid)}/comments/${encodeURIComponent(
        commentId,
      )}/find-similar`,
    ),

  applySimilar: (
    rid: string,
    commentId: string,
    targets: Array<{ rid: string; persona_id: string; watch_for_index: number }>,
  ) =>
    apiFetch<ReviewComment[]>(
      `/api/runs/${encodeURIComponent(rid)}/comments/${encodeURIComponent(
        commentId,
      )}/apply-similar`,
      {
        method: 'POST',
        body: JSON.stringify({ targets }),
      },
    ),

  // ──────────────────────────────────────────────────────────────
  // Registers (rulebook / conversation_design / verbalism + vardict)
  // ──────────────────────────────────────────────────────────────

  getCellRegister: (cell: string, type: RegisterType) =>
    apiFetch<RegisterContent>(
      `/api/registers/cell/${encodeURIComponent(cell)}/${type}`,
    ),

  putCellRegister: (cell: string, type: RegisterType, body: string) =>
    apiFetch<RegisterContent>(
      `/api/registers/cell/${encodeURIComponent(cell)}/${type}`,
      { method: 'PUT', body: JSON.stringify({ body }) },
    ),

  getVardict: (bank: 'ENBD' | 'EI') =>
    apiFetch<VarDictContent>(`/api/registers/bank/${bank}/variable_dictionary`),

  putVardict: (bank: 'ENBD' | 'EI', body: string) =>
    apiFetch<VarDictContent>(
      `/api/registers/bank/${bank}/variable_dictionary`,
      { method: 'PUT', body: JSON.stringify({ body }) },
    ),

  // ──────────────────────────────────────────────────────────────
  // Persona edit
  // ──────────────────────────────────────────────────────────────

  putPersona: (
    cell: string,
    lang: Language,
    pid: string,
    patch: Partial<PersonaDetail>,
  ) =>
    apiFetch<PersonaDetail>(
      `/api/personas/${encodeURIComponent(cell)}/${lang}/${encodeURIComponent(pid)}`,
      { method: 'PUT', body: JSON.stringify(patch) },
    ),
};
